import { decodeVoiceIdentity, stripTransportPrefix } from "@vidimo/shared";

/**
 * Single source of truth for what an inbound caller looks like once the raw
 * Twilio invite fields have been interpreted. The native bridge hands us a
 * loose bag of strings (`from`, custom params) whose trustworthiness varies
 * by platform — Android overwrites `from` with the friendly caller name, so
 * the lookup key has to come from a custom param instead. We resolve all of
 * that **once**, here, so the rest of the app (and the UI) works on clean,
 * already-decided attributes rather than re-parsing the same strings.
 */

type CallTransport = "whatsapp" | "phone";

export interface ParsedCaller {
  /** Same number with any `whatsapp:` prefix stripped, for display in the UI. */
  displayNumber: string | null;
  /**
   * Trusted lookup key for `useCallerLookup` — the raw E.164 from the custom
   * param, falling back to `from` for dev sims / un-normalized callers. Null
   * for staff calls (no phone→guest mapping).
   */
  e164: string | null;
  /** External = PSTN/WhatsApp guest; staff = staff-to-staff client call. */
  kind: "external" | "staff";
  /**
   * The property the call rang for, decoded from the dialed
   * `<member>__<property>` identity in `to`; the app may be scoped to
   * another one.
   */
  propertyId: string | null;
  /** Set only when `kind === "staff"` — the caller's membership id. */
  staffMemberId?: string;
  /**
   * Set only when `kind === "staff"` and the call carried the caller's name:
   * shown until their staff profile has synced to this device.
   */
  staffName?: string;
  /** WhatsApp vs Phone, fully resolved (never left ambiguous). */
  transport: CallTransport;
}

const WHATSAPP_PREFIX = "whatsapp:";

export interface RawInvite {
  customParams?: Record<string, string>;
  from?: string;
  to?: string;
}

/**
 * Parse the raw `callInviteReceived` payload into a {@link ParsedCaller}.
 *
 * - Staff-to-staff invites carry the caller's `<member>__<property>`
 *   identity in `CapacitorTwilioCallerIdentity`, or in `from`, with or
 *   without the `client:` prefix the plugins strip. Android replaces `from`
 *   with `CapacitorTwilioCallerName` when that is sent, so `from` is then a
 *   name and never decoded. Only the two-part shape is a teammate.
 * - For external callers the lookup key prefers the
 *   `CapacitorTwilioCallerE164` param (Android clobbers `from`), and the
 *   transport prefers `CapacitorTwilioCallerTransport`, falling back to the
 *   `from` prefix and finally to "phone".
 */
export function parseCaller({
  from,
  customParams,
  to,
}: RawInvite): ParsedCaller {
  const propertyId = decodeVoiceIdentity(to)?.propertyId ?? null;
  const fromIsCallerName =
    customParams?.CapacitorTwilioCallerName !== undefined;
  const decoded = decodeVoiceIdentity(
    customParams?.CapacitorTwilioCallerIdentity ??
      (fromIsCallerName ? null : from)
  );
  if (decoded?.propertyId) {
    return {
      displayNumber: null,
      e164: null,
      kind: "staff",
      propertyId,
      staffMemberId: decoded.memberId,
      staffName: customParams?.CapacitorTwilioCallerName?.trim() || undefined,
      transport: "phone",
    };
  }

  const e164 = customParams?.CapacitorTwilioCallerE164 ?? from ?? null;
  const transportParam = customParams?.CapacitorTwilioCallerTransport;
  let transport: CallTransport;
  if (transportParam === "whatsapp") {
    transport = "whatsapp";
  } else if (transportParam === "phone") {
    transport = "phone";
  } else if (from?.startsWith(WHATSAPP_PREFIX)) {
    transport = "whatsapp";
  } else {
    transport = "phone";
  }
  const displayNumber = e164 ? stripTransportPrefix(e164) || null : null;

  return {
    displayNumber,
    e164,
    kind: "external",
    propertyId,
    transport,
  };
}
